import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Paper,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ExpandedRowContent, DetailSection } from './ExpandedRowContent';
import { EventTimeline } from './EventTimeline';
import { fetchIndividualEmailDetails } from 'api/emailHistory/individualEmailsApi';

/**
 * @component ExpandedRowIndividual
 * @description Expanded row content for individual email details
 * @param {Object} props
 * @param {number} props.individualEmailId - Individual email ID
 * @param {function} [props.onNavigateToContact] - Navigation handler for contact view
 * @returns {JSX.Element}
 */
export function ExpandedRowIndividual({ individualEmailId, onNavigateToContact }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchIndividualEmailDetails(individualEmailId);
        setDetails(data);
      } catch (err) {
        console.error('Error fetching individual email details:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadDetails();
  }, [individualEmailId]);

  return (
    <ExpandedRowContent
      loading={loading}
      error={error}
      isEmpty={!details}
      htmlContent={details?.emailContents}
      showEmailPreview={true}
    >
      {/* Recipient Details */}
      <DetailSection title="Recipient Details">
        <Typography variant="body1">
          {details?.contactName || 'Unknown Contact'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {details?.emailAddress}
        </Typography>
        {details?.contactId && onNavigateToContact && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            endIcon={<OpenInNewIcon />}
            onClick={() => onNavigateToContact(details.contactId)}
            sx={{ mt: 1 }}
          >
            View Contact
          </Button>
        )}
      </DetailSection>

      {/* Event Timeline */}
      <DetailSection title="Email Event Journey">
        <Paper variant="outlined" sx={{ p: 2 }}>
          <EventTimeline status={details?.status} events={details?.events || []} />
        </Paper>
      </DetailSection>

      {/* Failure Reason (if applicable) */}
      {details?.failureReason && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>Delivery Failed</AlertTitle>
          {details.failureReason}
        </Alert>
      )}
    </ExpandedRowContent>
  );
}

